import React from 'react';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import AddSourceMenuButton from '../../../components/menuButton/AddSourceMenuButton';
import CollectionMaterialFileImportDialog from './CollectionMaterialFileImportDialog';
import CollectionMaterialUrlImportDialog from './CollectionMaterialUrlImportDialog';
import CollectionMaterialPasteImportDialog from './CollectionMaterialPasteImportDialog';

const OPTIONS = [
    {key: 'file', label: 'Files', icon: <AttachFileRoundedIcon fontSize="small"/>},
    {key: 'url', label: 'URL', icon: <LinkRoundedIcon fontSize="small"/>},
    {key: 'paste', label: 'Raw text', icon: <NotesRoundedIcon fontSize="small"/>},
];

/**
 * "Add Material" trigger + dedicated dialogs for the Collection Material entry points
 * (Files / URL / Raw text). Bundles the shared `AddSourceMenuButton` trigger with local
 * "which dialog is open" state so the host page (`CollectionTaskDetailPage`) only needs
 * to wire up `onImport`/`busy`/`initialClassificationLevel` (plus distribution/compartment),
 * matching the prop contract of the previous single tabbed dialog.
 *
 * `dropRequest` (optional `{files, token}`) lets the host page open the Files dialog
 * pre-populated from a drag&drop onto the Collected Materials section — mirrors Sources'
 * `openAddSourceDialogWithFiles`. `token` must increment on every drop so the effect fires
 * even when dropping the exact same file twice in a row.
 */
export default function AddCollectionMaterialControl({
    onImport,
    busy = false,
    initialClassificationLevel = 'U',
    initialDistribution = [],
    initialCompartment = [],
    disabled = false,
    collectionTaskId,
    dropRequest = null,
}) {
    const [openKind, setOpenKind] = React.useState(null);
    const [initialFiles, setInitialFiles] = React.useState(null);
    const lastDropTokenRef = React.useRef(null);

    React.useEffect(() => {
        if (dropRequest && dropRequest.token !== lastDropTokenRef.current && dropRequest.files?.length) {
            lastDropTokenRef.current = dropRequest.token;
            setInitialFiles(dropRequest.files);
            setOpenKind('file');
        }
    }, [dropRequest]);

    const handleClose = React.useCallback(() => {
        if (busy) return;
        setOpenKind(null);
        setInitialFiles(null);
    }, [busy]);

    const handleSelectMenu = React.useCallback((kind) => {
        setInitialFiles(null);
        setOpenKind(kind);
    }, []);

    return (
        <>
            <AddSourceMenuButton
                label="Add Material"
                disabled={disabled}
                onSelect={handleSelectMenu}
                options={OPTIONS}
            />
            <CollectionMaterialFileImportDialog
                open={openKind === 'file'}
                onClose={handleClose}
                busy={busy}
                initialClassificationLevel={initialClassificationLevel}
                initialDistribution={initialDistribution}
                initialCompartment={initialCompartment}
                collectionTaskId={collectionTaskId}
                initialFiles={initialFiles}
            />
            <CollectionMaterialUrlImportDialog
                open={openKind === 'url'}
                onClose={handleClose}
                onImport={onImport}
                busy={busy}
                initialClassificationLevel={initialClassificationLevel}
                initialDistribution={initialDistribution}
                initialCompartment={initialCompartment}
            />
            <CollectionMaterialPasteImportDialog
                open={openKind === 'paste'}
                onClose={handleClose}
                onImport={onImport}
                busy={busy}
                initialClassificationLevel={initialClassificationLevel}
                initialDistribution={initialDistribution}
                initialCompartment={initialCompartment}
            />
        </>
    );
}
